import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import "./SearchableSelect.component.css";

function normalizeSelectedValues(value, multiple) {
  if (multiple) {
    return Array.isArray(value) ? value : [];
  }

  return value ?? "";
}

function buildMultiValueLabel(selectedOptions) {
  if (selectedOptions.length === 0) {
    return "";
  }

  if (selectedOptions.length <= 2) {
    return selectedOptions.map((option) => option.label).join(", ");
  }

  return `${selectedOptions.length} selected`;
}

export default function SearchableSelect({
  id,
  options,
  value,
  onChange,
  placeholder,
  multiple,
  clearButtonLabel,
  filterPlaceholder,
  noResultsText,
  listAriaLabel,
  ariaDescribedBy,
  disabled,
  fullWidth,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const filterInputRef = useRef(null);
  const listRef = useRef(null);

  const normalizedValue = normalizeSelectedValues(value, multiple);
  const selectedValues = multiple ? normalizedValue : [normalizedValue].filter(Boolean);
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const listId = `${id}-listbox`;
  const statusId = `${id}-status`;

  const selectedOptions = useMemo(() => {
    const selectedSet = new Set(selectedValues);
    return options.filter((option) => selectedSet.has(option.value));
  }, [options, selectedValues]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === normalizedValue) ?? null,
    [options, normalizedValue]
  );

  const filteredOptions = useMemo(() => {
    if (!filterText) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(filterText.toLowerCase()));
  }, [filterText, options]);

  const resultCount = filteredOptions.length;
  const hasValue = multiple ? selectedOptions.length > 0 : Boolean(selectedOption);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setFilterText("");
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex];
      if (item) {
        item.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  const open = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
    setHighlightedIndex(0);
  }, [disabled]);

  const close = useCallback(() => {
    setIsOpen(false);
    setFilterText("");
    setHighlightedIndex(-1);
    requestAnimationFrame(() => {
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    });
  }, []);

  useEffect(() => {
    if (disabled && isOpen) {
      close();
    }
  }, [disabled, isOpen, close]);

  const handleSelect = useCallback(
    (option) => {
      if (disabled) {
        return;
      }
      if (multiple) {
        const currentValues = Array.isArray(normalizedValue) ? normalizedValue : [];
        const selectedSet = new Set(currentValues);
        if (selectedSet.has(option.value)) {
          selectedSet.delete(option.value);
        } else {
          selectedSet.add(option.value);
        }
        onChange(Array.from(selectedSet));
        return;
      }

      onChange(option.value);
      close();
    },
    [close, disabled, multiple, normalizedValue, onChange]
  );

  const handleClear = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }

      onChange(multiple ? [] : "");
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    },
    [multiple, onChange]
  );

  const navigateList = useCallback(
    (e) => {
      const count = filteredOptions.length;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (count === 0) return -1;
          return prev < count - 1 ? prev + 1 : 0;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (count === 0) return -1;
          return prev > 0 ? prev - 1 : count - 1;
        });
      } else if (e.key === "Home") {
        e.preventDefault();
        if (count > 0) setHighlightedIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        if (count > 0) setHighlightedIndex(count - 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < count) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "Tab") {
        close();
      }
    },
    [close, filteredOptions, handleSelect, highlightedIndex]
  );

  const handleTriggerKeyDown = useCallback(
    (e) => {
      if (disabled) {
        return;
      }
      if (isOpen) {
        navigateList(e);
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        open();
      } else if ((e.key === "Delete" || e.key === "Backspace") && hasValue) {
        e.preventDefault();
        handleClear();
      }
    },
    [disabled, handleClear, hasValue, isOpen, navigateList, open]
  );

  const activeDescendant =
    isOpen && highlightedIndex >= 0
      ? `${id}-option-${highlightedIndex}`
      : undefined;

  const displayValue = multiple
    ? buildMultiValueLabel(selectedOptions)
    : selectedOption?.label;

  return (
    <div
      className={`searchable-select${disabled ? " searchable-select--disabled" : ""}${fullWidth ? " searchable-select--full-width" : ""}`}
      ref={containerRef}
    >
      <div className="searchable-select__control">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          disabled={disabled}
          className={`searchable-select__trigger${hasValue && !disabled ? " searchable-select__trigger--with-clear" : ""}`}
          aria-labelledby={`${labelId} ${valueId}`}
          aria-describedby={ariaDescribedBy || undefined}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listId}
          onClick={() => {
            if (disabled) {
              return;
            }
            if (isOpen) {
              close();
            } else {
              open();
            }
          }}
          onKeyDown={handleTriggerKeyDown}
        >
          <span id={valueId} className={hasValue ? "searchable-select__value" : "searchable-select__placeholder"}>
            {displayValue || placeholder}
          </span>
        </button>
        {hasValue && !disabled && (
          <button
            type="button"
            className="searchable-select__clear"
            aria-label={clearButtonLabel}
            onClick={handleClear}
          >
            <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14">
              <path d="M14 1.41 12.59 0 7 5.59 1.41 0 0 1.41 5.59 7 0 12.59 1.41 14 7 8.41 12.59 14 14 12.59 8.41 7z" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>

      {isOpen && (
        <div className="searchable-select__dropdown">
          <input
            ref={filterInputRef}
            type="text"
            role="combobox"
            className="searchable-select__filter"
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setHighlightedIndex(0);
            }}
            onKeyDown={navigateList}
            placeholder={filterPlaceholder}
            aria-labelledby={labelId}
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={activeDescendant}
            aria-describedby={statusId}
            autoComplete="off"
          />
          <ul
            id={listId}
            ref={listRef}
            className="searchable-select__list"
            role="listbox"
            aria-label={listAriaLabel}
            aria-multiselectable={multiple ? "true" : undefined}
          >
            {filteredOptions.map((option, index) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <li
                  key={option.value}
                  id={`${id}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  className={`searchable-select__option${
                    index === highlightedIndex
                      ? " searchable-select__option--highlighted"
                      : ""
                  }${
                    isSelected
                      ? " searchable-select__option--selected"
                      : ""
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(option);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option.label}
                </li>
              );
            })}
          </ul>
          {resultCount === 0 && (
            <div className="searchable-select__no-results" role="status">
              {noResultsText}
            </div>
          )}
          <div id={statusId} className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {resultCount > 0
              ? `${resultCount} result${resultCount !== 1 ? "s" : ""} available.`
              : noResultsText}
          </div>
        </div>
      )}
    </div>
  );
}

SearchableSelect.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  clearButtonLabel: PropTypes.string,
  filterPlaceholder: PropTypes.string,
  noResultsText: PropTypes.string,
  listAriaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

SearchableSelect.defaultProps = {
  value: "",
  placeholder: "Select option...",
  multiple: false,
  clearButtonLabel: "Clear selection",
  filterPlaceholder: "Type to filter...",
  noResultsText: "No results found",
  listAriaLabel: "Select options",
  ariaDescribedBy: undefined,
  disabled: false,
  fullWidth: false,
};
